import React, { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Draggable } from '@hello-pangea/dnd';
import { motion } from 'framer-motion';
import { MoreIcon } from 'utils/icons';
import DropDownMenu from 'components/DropDownMenu';
import FormInput from 'components/FormInput';
import { formatCurrency, stopPropagation } from 'utils/helper';
import {
  getCategoryMenuOptionLabel,
  isCategoryMenuEvent,
} from '../categoryMenuActions';

const ACCOUNT_NUMBER_PLACEHOLDER = 'Acct # +';

const getAccountNumberPillWidth = value => {
  const textLength = (value || ACCOUNT_NUMBER_PLACEHOLDER).length;
  return `calc(${Math.min(14, Math.max(1, textLength))}ch + 0.85rem)`;
};

const isSelectionModifierEvent = event =>
  Boolean(event?.shiftKey || event?.altKey || event?.metaKey || event?.ctrlKey);

const SwimlaneItem = memo(
  ({
    item,
    index,
    itemPositionIndex,
    itemId,
    droppableId,
    nestTargetState,
    isActive,
    isMultiSelected,
    menuTargetCount,
    editing,
    itemClass,
    allowToAddSubCategory,
    lineItemOptions,
    hasEditAccess,
    onItemClick,
    onNameChange,
    onOptionsChange,
    onSetEditing,
    swimlaneIndex,
    onKeyUp,
    renderedCost,
    presenceMarkers,
    flyFromSuggestionLayoutId,
    showAccountNumbers,
    onAccountNumberChange,
    selectNameOnEdit,
    onNameSelected,
  }) => {
    const editingKey = `${swimlaneIndex}-${item?.id}`;
    const domItemIndex = itemPositionIndex ?? index;
    const primaryPresence = presenceMarkers?.[0];
    const isPendingCreate = /^NEW_ITEM_/.test(String(item?.id || ''));
    const accountNumberValue = item?.account_number || '';
    const [accountNumberDraft, setAccountNumberDraft] =
      useState(accountNumberValue);
    const isEditingName = editing[editingKey];

    useEffect(() => {
      setAccountNumberDraft(accountNumberValue);
    }, [accountNumberValue]);
    const handleNameClick = e => {
      if (isActive && !isEditingName && hasEditAccess()) {
        stopPropagation(e);
        const newEditingState = { [editingKey]: !isEditingName };
        onSetEditing(newEditingState);
      }
    };

    const handleItemClick = e => {
      if (isCategoryMenuEvent(e)) {
        stopPropagation(e);
        return;
      }

      const closest = e.target.closest('.swimlane-item');
      closest?.focus();
      onItemClick(item)(e);
    };

    const handleItemMouseDown = e => {
      if (isCategoryMenuEvent(e)) {
        stopPropagation(e);
        return;
      }

      if (!isSelectionModifierEvent(e)) return;
      if (e.target.closest('input, textarea, select, [contenteditable]')) {
        return;
      }

      e.preventDefault();
    };

    const handleFormInputBlur = e => {
      // Add a small delay to prevent immediate blur events when starting to edit
      setTimeout(() => {
        const currentValue = e?.target?.value;
        if (currentValue !== item?.name) {
          onNameChange(item)(e);
        } else {
          // If no change, just exit edit mode
          onSetEditing({ [`${swimlaneIndex}-${item?.id}`]: false });
        }
      }, 100);
    };

    const handleFormInputKeyDown = e => {
      if (e.key === 'Enter') {
        onNameChange(item)(e);
      }
    };

    const handleFormInputFocus = e => {
      if (!selectNameOnEdit) return;

      e?.target?.select?.();
      onNameSelected?.(item);
    };

    const handleAccountNumberChange = e => {
      setAccountNumberDraft((e?.target?.value || '').slice(0, 14));
    };

    const handleAccountNumberBlur = () => {
      const value = accountNumberDraft.slice(0, 14);
      if (value !== accountNumberValue) {
        onAccountNumberChange?.(item)({ target: { value } });
      }
    };

    const handleAccountNumberKeyDown = e => {
      e.stopPropagation();
      if (e.key === 'Enter') {
        e.currentTarget.blur();
      }
    };

    const renderItemName = () => (
      <>
        {isEditingName ? (
          <span className="item-name">
            <FormInput
              defaultValue={item?.name}
              onBlur={handleFormInputBlur}
              onClick={e => stopPropagation(e)}
              onFocus={handleFormInputFocus}
              onKeyDown={handleFormInputKeyDown}
              autoFocus={true}
              stopOnChange={true}
              addSpacing={false}
              hiddenColumn={false}
              showAutofocus={false}
            />
          </span>
        ) : (
          <span className="item-name">{item?.name}</span>
        )}
        {showAccountNumbers && (
          <input
            className={`item-account-number-input ${
              accountNumberDraft.trim() ? 'has-value' : 'is-empty'
            }`}
            style={{ width: getAccountNumberPillWidth(accountNumberDraft) }}
            value={accountNumberDraft}
            placeholder={ACCOUNT_NUMBER_PLACEHOLDER}
            maxLength={14}
            disabled={!hasEditAccess()}
            onMouseDown={stopPropagation}
            onClick={e => stopPropagation(e)}
            onChange={handleAccountNumberChange}
            onBlur={handleAccountNumberBlur}
            onKeyDown={handleAccountNumberKeyDown}
          />
        )}
      </>
    );

    const filteredOptions = lineItemOptions
      ?.filter(
        lineItem =>
          !(lineItem.value === 'add-sub-category' && !allowToAddSubCategory)
      )
      .map(option => ({
        ...option,
        label: getCategoryMenuOptionLabel({
          option,
          targetCount: menuTargetCount,
        }),
      }));

    return (
      <Draggable
        key={itemId}
        draggableId={itemId}
        index={index}
        isDragDisabled={!hasEditAccess() || isPendingCreate}
      >
        {provided => (
          <div
            tabIndex="1"
            onKeyUp={onKeyUp}
            key={itemId}
            className={[
              'swimlane-item',
              itemClass,
              isMultiSelected ? 'swimlane-item--multi-selected' : '',
              nestTargetState ? `swimlane-item--nest-${nestTargetState}` : '',
            ]
              .filter(Boolean)
              .join(' ')}
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            data-category-draggable-id={itemId}
            data-category-droppable-id={droppableId}
            id={swimlaneIndex + ',' + domItemIndex}
            onClick={handleItemClick}
            onMouseDown={handleItemMouseDown}
            style={{
              ...provided.draggableProps.style,
              ...(primaryPresence
                ? { '--presence-color': primaryPresence.user_color }
                : {}),
            }}
            title={primaryPresence?.context?.breadcrumb_label}
          >
            {(isActive || isEditingName) && (
              <div className="swimlane-item--active" />
            )}
            {isMultiSelected && <div className="swimlane-item--selection" />}

            {primaryPresence && <div className="swimlane-item--presence" />}

            {flyFromSuggestionLayoutId ? (
              <>
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                  style={{ flex: 1, minWidth: 0 }}
                >
                  <div className="swimlane-item__info">
                    <div
                      className="item-name-wrapper"
                      onClick={handleNameClick}
                    >
                      {renderItemName()}
                    </div>

                    <div className="item-cost">
                      {formatCurrency(renderedCost || 0)}
                    </div>
                  </div>
                </motion.div>

                {hasEditAccess() && item?.id && (
                  <div
                    className="swimlane-item__options"
                    onClick={stopPropagation}
                    onMouseDown={stopPropagation}
                    onPointerDown={stopPropagation}
                    onPointerUp={stopPropagation}
                  >
                    <DropDownMenu
                      menuOptions={filteredOptions}
                      toggleIcon={<MoreIcon className="more-icon" />}
                      onChange={onOptionsChange(item)}
                    />
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="swimlane-item__info">
                  <div className="item-name-wrapper" onClick={handleNameClick}>
                    {renderItemName()}
                  </div>

                  <div className="item-cost">
                    {formatCurrency(renderedCost || 0)}
                  </div>
                </div>

                {hasEditAccess() && item?.id && (
                  <div
                    className="swimlane-item__options"
                    onClick={stopPropagation}
                    onMouseDown={stopPropagation}
                    onPointerDown={stopPropagation}
                    onPointerUp={stopPropagation}
                  >
                    <DropDownMenu
                      menuOptions={filteredOptions}
                      toggleIcon={<MoreIcon className="more-icon" />}
                      onChange={onOptionsChange(item)}
                    />
                  </div>
                )}
              </>
            )}
            {primaryPresence && (
              <div className="swimlane-item__presence">
                {presenceMarkers.slice(0, 3).map(presence => (
                  <span
                    key={`${presence.user}-${presence.id}`}
                    className="swimlane-item__presence-badge"
                    style={{ '--presence-color': presence.user_color }}
                    title={`${presence.user_name} is editing ${
                      presence.context?.breadcrumb_label || item?.name
                    }`}
                  >
                    {presence.user_initials}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </Draggable>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function to ensure editing state changes are detected
    const editingChanged = prevProps.editing !== nextProps.editing;
    const prevEditingKey = `${prevProps.swimlaneIndex}-${prevProps.item?.id}`;
    const nextEditingKey = `${nextProps.swimlaneIndex}-${nextProps.item?.id}`;
    const itemEditingChanged =
      prevProps.editing[prevEditingKey] !== nextProps.editing[nextEditingKey];
    const isActiveChanged = prevProps.isActive !== nextProps.isActive;
    const isMultiSelectedChanged =
      prevProps.isMultiSelected !== nextProps.isMultiSelected;
    const menuTargetCountChanged =
      prevProps.menuTargetCount !== nextProps.menuTargetCount;
    const presenceChanged =
      prevProps.presenceMarkers !== nextProps.presenceMarkers;

    // Return true if props are equal (don't re-render), false if different (re-render)
    return (
      !editingChanged &&
      !itemEditingChanged &&
      !isActiveChanged &&
      !isMultiSelectedChanged &&
      !menuTargetCountChanged &&
      !presenceChanged &&
      prevProps.item === nextProps.item &&
      prevProps.index === nextProps.index &&
      prevProps.itemId === nextProps.itemId &&
      prevProps.droppableId === nextProps.droppableId &&
      prevProps.nestTargetState === nextProps.nestTargetState &&
      prevProps.itemPositionIndex === nextProps.itemPositionIndex &&
      prevProps.itemClass === nextProps.itemClass &&
      prevProps.allowToAddSubCategory === nextProps.allowToAddSubCategory &&
      prevProps.lineItemOptions === nextProps.lineItemOptions &&
      prevProps.hasEditAccess === nextProps.hasEditAccess &&
      prevProps.onItemClick === nextProps.onItemClick &&
      prevProps.onNameChange === nextProps.onNameChange &&
      prevProps.onOptionsChange === nextProps.onOptionsChange &&
      prevProps.onSetEditing === nextProps.onSetEditing &&
      prevProps.swimlaneIndex === nextProps.swimlaneIndex &&
      prevProps.onKeyUp === nextProps.onKeyUp &&
      prevProps.renderedCost === nextProps.renderedCost &&
      prevProps.showAccountNumbers === nextProps.showAccountNumbers &&
      prevProps.onAccountNumberChange === nextProps.onAccountNumberChange &&
      prevProps.selectNameOnEdit === nextProps.selectNameOnEdit &&
      prevProps.onNameSelected === nextProps.onNameSelected &&
      prevProps.flyFromSuggestionLayoutId ===
        nextProps.flyFromSuggestionLayoutId
    );
  }
);

SwimlaneItem.displayName = 'SwimlaneItem';

SwimlaneItem.propTypes = {
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  itemPositionIndex: PropTypes.number,
  itemId: PropTypes.string.isRequired,
  droppableId: PropTypes.string.isRequired,
  nestTargetState: PropTypes.oneOf(['', 'pending', 'ready']),
  isActive: PropTypes.bool.isRequired,
  isMultiSelected: PropTypes.bool,
  menuTargetCount: PropTypes.number,
  editing: PropTypes.object.isRequired,
  itemClass: PropTypes.string.isRequired,
  allowToAddSubCategory: PropTypes.bool.isRequired,
  lineItemOptions: PropTypes.array.isRequired,
  hasEditAccess: PropTypes.func.isRequired,
  onItemClick: PropTypes.func.isRequired,
  onNameChange: PropTypes.func.isRequired,
  onOptionsChange: PropTypes.func.isRequired,
  onSetEditing: PropTypes.func.isRequired,
  swimlaneIndex: PropTypes.number.isRequired,
  onKeyUp: PropTypes.func.isRequired,
  renderedCost: PropTypes.number.isRequired,
  presenceMarkers: PropTypes.array,
  flyFromSuggestionLayoutId: PropTypes.string,
  showAccountNumbers: PropTypes.bool,
  onAccountNumberChange: PropTypes.func,
  selectNameOnEdit: PropTypes.bool,
  onNameSelected: PropTypes.func,
};

SwimlaneItem.defaultProps = {
  presenceMarkers: [],
  flyFromSuggestionLayoutId: null,
  itemPositionIndex: undefined,
  nestTargetState: '',
  showAccountNumbers: false,
  onAccountNumberChange: null,
  isMultiSelected: false,
  menuTargetCount: 1,
  selectNameOnEdit: false,
  onNameSelected: null,
};

export default SwimlaneItem;
